import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { TextArea } from "../components/common/TextArea";
import { TextBox } from "../components/common/TextBox";
import PopupContent from "../components/popupContent";
import ProductGroupSelector from "../components/products/ProductGroupSelector";
import { FormatString } from "../helpers/stringHelpers";
import TranslationContext from "../providers/TranslationContext";
import { ValidationProvider } from "../providers/ValidationContext";
import { addNotification } from "../redux/actions";

import {
  apiGetProductDetail,
  apiProductCreateOrUpdate,
} from "../services/ProductsService";
import { validateForm } from "../validation/formValidation";
import { model } from "../validation/models/product";

import PopupOperationsBar from "./PopupOperationsBar";
import Button from "../components/common/Button";
import ComboBoxData from "../components/common/ComboBoxData";
import CheckBox from "../components/common/CheckBox";
import { SelectBox } from "../components/common/SelectBox";

// styles
require("./PopupGeneralStyles.css");
require("./ProductsPopup.css");

interface Props {
  id?: number;
  handleCancel: any;
  refreshData: any;
  productGroups: never[];
  products: any[];
}

const ProductsPopup = (props: Props) => {
  const translationContext = useContext(TranslationContext) as any;
  const prefix = translationContext.prefix;
  const { t } = useTranslation(undefined, { keyPrefix: `${prefix}.Popup` });

  const { t: tDiscountsFields } = useTranslation(undefined, { keyPrefix: 'Discounts.Fields' });
  const cloneDeep = require('clone-deep');
  const [validationModel, setValidationModel] = useState(cloneDeep(model));

  const [loading, setLoading] = useState(true);
  const data: any = {};
  const [formData, setFormData] = useState(data);
  const [saveError, setSaveError] = useState(false);

  const isEditMode = props.id ? true : false;

  const productsForSorting: any = props.id ? props.products.filter(a => a.id != props.id) : null;
  const defaultProductForSorting = productsForSorting ? productsForSorting[0].id : 0;
  const [sortOrderOptions, setSortOrderOptions] = useState(
    {
      'changeSortOrder': false,
      'direction': 'before',
      'productId': defaultProductForSorting
    });


  const dispatch = useDispatch();

  useEffect(() => {
    if (props.id) {
      apiGetProductDetail(props.id).then((responseData) => {
        setFormData(responseData.data);

        setLoading(false);
      }).catch((err: any) => {
        dispatch(addNotification({ text: FormatString(t('LoadingDetailsError'), `${props.id}`), type: 'error' }));
      });
    } else {
      setLoading(false);
    }
  }, []);

  const onAddingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiProductCreateOrUpdate(formData)
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('CreateSuccess'), formData.name), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('CreateFailure'), formData.name), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onEditingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiProductCreateOrUpdate(
        {
          ...formData,
          UpdateSortOrder: sortOrderOptions.changeSortOrder,
          SortOrderDirectionIsBefore: sortOrderOptions.direction == "before",
          SortOrderProductId: sortOrderOptions.productId
        })
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('EditSuccess'), formData.name), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('CreatUpdateFailureeSuccess'), formData.name), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onCancelClick = () => {
    props.handleCancel();
  };

  const handleGroupSelectionChange = (productGroup: any) => {
    formData['idVyrobekSkupinaTree'] = productGroup.group.id;
    formData['path'] = productGroup.group.path;
    setFormData({ ...formData });
  }

  const handleProductChange = (value: number) => {
    setSortOrderOptions({ ...sortOrderOptions, productId: value });
  };

  return (
    <PopupContent additionalClassNames="productsPopup" header={props.id ? t('EditLabel') : t('CreateLabel')}>
      <ValidationProvider value={validationModel}>
        <TextBox
          dataField="name"
          defaultValue={formData.name}
          formData={formData}
          setStateMethod={setFormData}
          id="name"
          label={t('Labels.name')}
          placeholder={t('Labels.namePlaceholder')}
        />

        <SelectBox
          dataField="discountBaseGroupName"
          value={formData.discountBaseGroupName}
          formData={formData}
          label={t('Labels.DiscountBaseGroupName')}
          options={[
            { value: "-", label: "-" },
            { value: "discountBaseDekorativaDenas", label: tDiscountsFields('discountBaseDekorativaDenas') },
            { value: "discountBaseDekorativaBalt", label: tDiscountsFields('discountBaseDekorativaBalt') },
            { value: "discountBasePrumyslBaltHostemix", label: tDiscountsFields('discountBasePrumyslBaltHostemix') },
            { value: "discountBasePrumyslDenas", label: tDiscountsFields('discountBasePrumyslDenas') },
            { value: "discountBasePurlet", label: tDiscountsFields('discountBasePurlet') },
            { value: "discountBaseNezarazeno", label: tDiscountsFields('discountBaseNezarazeno') }]}

          setStateMethod={setFormData}
          isDisabled={false}
          placeholder={""} />

        <ProductGroupSelector
          productGroups={props.productGroups}
          productId={formData.id}
          selectedProductGroupId={formData.idVyrobekSkupinaTree}
          selectedProductGroupName={formData.path}
          label={t('Labels.productsGroup')}
          onGroupSelectionChange={handleGroupSelectionChange}
          dataField={'idVyrobekSkupinaTree'}
        />

        <CheckBox
          id="preferLitres"
          label={`${t('Labels.preferLitres')}`}
          paddingLeft={"10px"}
          handleCheckedChange={(checked) => {

            setFormData({ ...formData, preferLitres: checked });
          }}
          defaultValue={formData.preferLitres}
        />

        <TextArea
          id="noteInternal"
          label={t('Labels.noteInternal')}
          placeholder={t('Labels.noteInternalPlaceholder')}
          rows={4}
          dataField="noteInternal"
          value={formData.noteInternal}
          formData={formData}
          setStateMethod={setFormData}
        />

        {props.id && <>
          <CheckBox
            id="changeSortOrder"
            label={`${t('Labels.changeSortOrder')}`}
            paddingLeft={"10px"}
            handleCheckedChange={(checked) => {

              setSortOrderOptions({ ...sortOrderOptions, changeSortOrder: checked });
            }}
            defaultValue={false}
          />

          {sortOrderOptions.changeSortOrder &&
            <div className="sortOrderOptions">
              <Button iconClass="" bold={sortOrderOptions.direction == 'before'} onClick={() => { setSortOrderOptions({ ...sortOrderOptions, direction: 'before' }) }} text={t('BeforeProduct')} />
              <Button iconClass="" bold={sortOrderOptions.direction == 'after'} onClick={() => { setSortOrderOptions({ ...sortOrderOptions, direction: 'after' }) }} text={t('AfterProduct')} />

              <ComboBoxData
                id="products"
                label={t('Labels.product')}
                dataTextField="name"
                dataValueField="id"
                defaultValue={defaultProductForSorting}
                handleChange={handleProductChange}
                rows={productsForSorting}
              />
            </div>
          }
        </>}

        <PopupOperationsBar
          handleAdding={onAddingClick}
          handleCancellation={onCancelClick}
          handleEditing={onEditingClick}
          id={props.id}
        />
      </ValidationProvider>
    </PopupContent>
  );
};

export default ProductsPopup;
